export const SN_REGEX = /^((00|\+)?421|0)?(\d{3})(\d{3})(\d{3})$/;
export const NBSP = '\xa0';
export const BINARY_BASE = 2 ** 10;

/**
 * Format SN number in various formats
 *
 * @param {string} sn
 * @param {boolean} spaces
 * @param {string} prefix
 * @return {string|null}
 */
export function formatSn(sn, spaces = true, prefix = '0') {
  if (typeof sn !== 'string' || !SN_REGEX.test(sn)) {
    return null;
  }

  if (typeof spaces !== 'boolean') {
    return null;
  }

  if (typeof prefix !== 'string') {
    return null;
  }

  const [, , , group1, group2, group3] = SN_REGEX.exec(sn);

  const space = spaces ? NBSP : '';
  const prefixSpace = spaces && prefix !== '0' ? NBSP : '';

  return `${prefix}${prefixSpace}${group1}${space}${group2}${space}${group3}`;
}

/**
 * If the nickname if a phone number it get's formatted otherwise
 *
 * @param {string} identNick
 * @param {boolean} spaces
 * @param {string} prefix
 * @return {string|null}
 */
export function formatNickname(identNick, spaces = true, prefix = '0') {
  if (typeof identNick !== 'string') {
    return null;
  }
  if (SN_REGEX.test(identNick)) {
    return formatSn(identNick, spaces, prefix);
  }
  return identNick;
}

/**
 * Checks if provided locale is valid according to `Intl`
 *
 * @param {string} locale
 * @returns {boolean}
 */
function isValidLocale(locale) {
  return typeof locale === 'string' && Intl.DateTimeFormat.supportedLocalesOf(locale).length !== 0;
}

/**
 * Format a date according to selected locale.
 * All spaces contained in formatted date are non breaking spaces
 *
 * @param {string|Date} date
 * @param {string} locale
 * @return {string|null}
 */
export function formatDate(date, locale = 'sk-SK') {
  if (typeof date !== 'string' && !(date instanceof Date)) {
    return null;
  }

  const dateObject = date instanceof Date ? date : new Date(date);

  const formatter = new Intl.DateTimeFormat(isValidLocale(locale) ? locale : 'en-US');
  return formatter.format(dateObject).replace(/\s/g, NBSP); // Replaces spaces for non breaking spaces
}

/**
 * Format time according to selected locale.
 *
 * @param {string|Date} dateTime
 * @param {string} locale
 * @return {string|null}
 */
export function formatTime(dateTime, locale = 'sk-SK') {
  if (typeof dateTime !== 'string' && !(dateTime instanceof Date)) {
    return null;
  }

  const dateObject = dateTime instanceof Date ? dateTime : new Date(dateTime);

  const formatter = new Intl.DateTimeFormat(isValidLocale(locale) ? locale : 'en-US', {
    hour: 'numeric',
    minute: 'numeric',
  });
  return formatter.format(dateObject);
}

/**
 * Format time duration.
 *
 * @param {number} seconds
 * @return {string|null}
 */
export function formatDuration(seconds) {
  if (typeof seconds !== 'number' && !Number.isNaN(seconds) && parseInt(seconds, 10) === seconds) {
    return null;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const sec = seconds - hours * 3600 - minutes * 60;

  // TODO validate locale with isValidLocale(locale) ? locale : 'sk-SK' and add locale to function params

  if (hours) {
    return `${hours} hod., ${minutes} min.`; // TODO: i18n
  }

  return `${minutes} min., ${sec} s.`;
}

/**
 * Format date-time according to selected locale.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * @param {string|Date} dateTime
 * @param {string} locale
 * @param {?string} todayAlias
 * @return {string|null}
 */
export function formatDateTime(dateTime, locale = 'sk-SK', todayAlias = 'Dnes') {
  if (typeof dateTime !== 'string' && !(dateTime instanceof Date)) {
    return null;
  }

  const today = new Date();
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

  if (
    today.getYear() === date.getYear() &&
    today.getDate() === date.getDate() &&
    today.getMonth() === date.getMonth() &&
    todayAlias
  ) {
    const formatter = new Intl.DateTimeFormat(isValidLocale(locale) ? locale : 'en-US', {
      hour: 'numeric',
      minute: 'numeric',
    });

    return `${todayAlias} ${formatter.format(date)}`;
  }

  const formatter = new Intl.DateTimeFormat(isValidLocale(locale) ? locale : 'en-US', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  return formatter
    .formatToParts(date)
    .map(({ type, value }) => {
      switch (type) {
        case 'literal':
          // Strip `, ` literals dividing date and time
          return value === ', ' ? ' ' : value;
        default:
          return value;
      }
    })
    .join('');
}

/**
 * Format currency according to selected locale.
 * Displays negative sign for negative provided values except negative zero.
 *
 * @param {number|string} value
 * @param {string} locale
 * @param {string} currency
 * @param {number} fractionDigits
 * @return {string|null}
 */
export function formatCurrency(value, locale = 'sk-SK', currency = 'EUR', fractionDigits = 2) {
  const isValueValid =
    (typeof value === 'number' && !Number.isNaN(value)) ||
    (typeof value === 'string' && !Number.isNaN(Number(value)) && value.trim() !== '');

  if (!isValueValid) {
    return null;
  }

  if (typeof currency !== 'string') {
    return null;
  }

  if (typeof fractionDigits !== 'number' || fractionDigits < 0) {
    return null;
  }

  const formatter = new Intl.NumberFormat(isValidLocale(locale) ? locale : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });

  return formatter.format(Object.is(value, -0) ? 0 : value);
}

/**
 * Return rules for rendering of price.
 * Determines, which price parts should be rendered.
 *
 * @param {?number} cycle
 * @param {?number} purchase
 * @returns {{cycle: boolean, purchase: boolean}}
 */
export function getPriceRenderRules({ cycle = null, purchase = null }) {
  if (cycle === null && purchase === null) {
    return {
      cycle: false,
      purchase: false,
    };
  }

  if (
    (cycle === 0 && purchase === 0) ||
    (cycle !== null && (purchase === 0 || purchase === null))
  ) {
    return {
      cycle: true,
      purchase: false,
    };
  }

  if (cycle !== null && (purchase !== 0 || purchase !== null)) {
    return {
      cycle: true,
      purchase: true,
    };
  }

  return {
    cycle: false,
    purchase: true,
  };
}

/**
 * Format cycle and activation price of the service as follows:
 *
 * When period is not specified (e.g. postpaid service tied to a billing cycle):
 * cycle null, purchase null    →  [empty string]
 * cycle null, purchase 0       →  0,00€
 * cycle 0, purchase 0 or null  →  0,00€
 * cycle 1, purchase 0 or null  →  1,00€/mes.
 * cycle 0 or null, purchase 1  →  1,00€ (Aktivácia)
 * cycle 1, purchase 1          →  1,00€/mes. + 1,00€ (Aktivácia)
 *
 * When period is specified (e.g. prepaid service with specified lifetime):
 * cycle null, purchase null, period 30 days    →  [empty string]
 * cycle null, purchase 0, period 30 days       →  0,00€/30 dní
 * cycle 0, purchase 0 or null, period 30 days  →  0,00€
 * cycle 1, purchase 0 or null, period 30 days  →  1,00€/každých 30 dní
 * cycle 0 or null, purchase 1, period 30 days  →  1,00€/30 dní
 * cycle 1, purchase 1, period 30 days          →  1,00€/každých 30 dní + jednorazová aktivácia: 1,00€
 *
 * @param {?number} cycle - cost for the service paid each month
 * @param {?number} purchase - one time payment, so-called activation cost of the service
 * @param {boolean} hasBrackets - whether activation cost should have brackets around or not
 * @param {string} locale
 * @param {string} currency
 * @param {?Period} period
 * @param {number} fractionDigits
 * @return {string|null}
 */
export function formatActivationAndCyclePrice({
  cycle = null,
  purchase = null,
  hasBrackets = true,
  locale = 'sk-SK',
  currency = 'EUR',
  period = null,
  fractionDigits = 2,
}) {
  const cyclePeriodLabel = period ? `/${period.getLabel(true)}` : '/mes.';
  const purchasePeriodLabel = period ? `/${period.getLabel(false)}` : '';
  const activationLabel = (() => {
    if (period) {
      return '';
    }
    return hasBrackets ? ' (Aktivácia)' : ' aktivácia';
  })();

  const cyclePrice = formatCurrency(cycle, locale, currency, fractionDigits);
  const labeledCyclePrice = `${cyclePrice}${cyclePeriodLabel}`;

  const purchasePrice = formatCurrency(purchase, locale, currency, fractionDigits);
  const labeledPurchasePrice = `${purchasePrice}${activationLabel}${purchasePeriodLabel}`;

  const hasCyclePrice = cycle !== 0 && cycle !== null;
  const hasPurchasePrice = purchase !== 0 && purchase !== null;

  if (hasCyclePrice && hasPurchasePrice) {
    if (period) {
      return `${labeledCyclePrice} + jednorazová aktivácia: ${purchasePrice}`;
    }
    return `${labeledCyclePrice} + ${labeledPurchasePrice}`;
  }

  if (hasCyclePrice && !hasPurchasePrice) {
    return labeledCyclePrice;
  }

  if (!hasCyclePrice && hasPurchasePrice) {
    return labeledPurchasePrice;
  }

  if (cycle === null && purchase === 0) {
    return `${purchasePrice}${purchasePeriodLabel}`;
  }

  if (cycle === 0 && !hasPurchasePrice) {
    return cyclePrice;
  }

  return null;
}

/**
 * Format numbers according to selected locale.
 *
 * @param {number|string} value
 * @param {string} locale
 * @param {number} maximumFractionDigits
 * @return {string|null}
 */
export function formatNumber(value, locale = 'sk-SK', maximumFractionDigits = 2) {
  const isValueValid =
    (typeof value === 'number' && !Number.isNaN(value)) ||
    (typeof value === 'string' && !Number.isNaN(Number(value)) && value.trim() !== '');

  if (!isValueValid) {
    return null;
  }

  if (typeof maximumFractionDigits !== 'number' || maximumFractionDigits < 0) {
    return null;
  }

  const formatter = new Intl.NumberFormat(isValidLocale(locale) ? locale : 'en-US', {
    maximumFractionDigits,
  });

  return formatter.format(value);
}

/**
 * Determine the practical unit that should be used to display given number of bytes, with focus on keeping
 * the decimal representation bellow 4 digits.
 *
 * The "unit" number represents the 2^10 (1024) step - i.e. 0 is B, 1 is kB, 2 is MB etc.
 * Real unit would be chosen mathematically - 1023 B would be 0 (B), 1025 B would cross the threshold and be 1 (kB).
 * However, for practical reasons this rollover point is moved to account for decimal representation
 * so that it happens when the decimal representation crosses three digits, so that 1000 B would return 1 (kB).
 *
 *
 * @param bytes
 * @return {number}
 */
export function getPracticalBytesUnit(bytes) {
  const realUnit = Math.floor(Math.log2(bytes) / 10);

  const shortenedDecimalRepresentation = bytes / 2 ** (10 * realUnit);

  if (shortenedDecimalRepresentation / 1000 >= 1) {
    return realUnit + 1;
  }
  return realUnit;
}

/**
 * Format data size to appropriate units.
 * Goal is to display any number in less than 4 decimal places (in base 10).
 *
 * @param {number} bytes - size in bytes
 * @param {string} locale - BCP 47 (rfc5646) language tag
 * @param {?number} numOfDecimals - number of decimal places
 * @param {boolean} showUnits
 * @return {string}
 */
export function formatBytes(bytes, locale = 'sk-SK', numOfDecimals = null, showUnits = true) {
  const unitStrings = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (bytes === 0) {
    return showUnits ? `0${NBSP}${unitStrings[0]}` : '0';
  }

  const unit = getPracticalBytesUnit(bytes);

  // In case `decimals` are not explicitly defined, we set decimals to 1 by default from GB unit, and to 0 otherwise
  const decimals = numOfDecimals ?? (unit > 2 ? 1 : 0);

  const value = bytes / 2 ** (unit * 10);

  const number = value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return showUnits ? `${number}${NBSP}${unitStrings[unit]}` : number;
}

/**
 * Extension of `formatBytes` method that allows specifying the input order exponent (of 1024).
 * E.g. 0 for bytes, 1 for kilobytes, * 2 for megabytes etc.
 *
 * @see formatBytes
 * @param {number} amount
 * @param {?number} exponent
 * @param {?string} locale
 * @param {?number} decimals
 * @param {boolean} showUnits
 * @return {string}
 */
export function formatData({
  amount,
  exponent = 1,
  locale = 'sk-SK',
  decimals = null,
  showUnits = true,
}) {
  const bytes = amount * BINARY_BASE ** exponent;
  return formatBytes(bytes, locale, decimals, showUnits);
}

/**
 * @param {number} value
 * @param {boolean} showUnits
 * @return {string}
 */
export function formatVoiceDuration(value, showUnits = true) {
  const unit = value >= 60 ? 'min.' : 's.';
  const valueRounded = Math.floor(value / (value >= 60 ? 60 : 1));
  return showUnits ? `${valueRounded} ${unit}` : `${valueRounded}`;
}
